import axios from '@/utils/axios';
import {
  NotificationListRequest,
  NotificationListResponse,
  NotificationPagination,
  NotificationResponse,
  NotificationObjectProps,
  NotificationReadAllResponse,
} from '../types';
import { hasRenderableNotification } from '../utils';

const DEFAULT_NOTIFICATIONS_PAGE = 1;
const DEFAULT_NOTIFICATIONS_LIMIT = 25;
const MAX_NOTIFICATIONS_LIMIT = 100;

function normalizeNotifications(
  notifications: NotificationObjectProps[] | null | undefined
) {
  if (!Array.isArray(notifications)) return [];

  return notifications.filter(hasRenderableNotification);
}

const normalizePositiveInteger = (value: unknown, fallback: number) => {
  const parsedValue = Number(value);

  if (!Number.isFinite(parsedValue) || parsedValue < 1) return fallback;

  return Math.trunc(parsedValue);
};

const normalizeNotificationLimit = (limit?: number) =>
  Math.min(
    normalizePositiveInteger(limit, DEFAULT_NOTIFICATIONS_LIMIT),
    MAX_NOTIFICATIONS_LIMIT
  );

const buildFallbackPagination = ({
  page,
  limit,
  total,
}: {
  page: number;
  limit: number;
  total: number;
}): NotificationPagination => {
  const totalPages = total > 0 ? Math.ceil(total / limit) : 0;

  return {
    page,
    limit,
    total,
    totalPages,
    hasNextPage: totalPages > 0 && page < totalPages,
    hasPreviousPage: page > 1 && totalPages > 0,
  };
};

export async function getNotificationRequest({
  page = DEFAULT_NOTIFICATIONS_PAGE,
  limit = DEFAULT_NOTIFICATIONS_LIMIT,
}: NotificationListRequest = {}): Promise<NotificationListResponse> {
  const normalizedPage = normalizePositiveInteger(
    page,
    DEFAULT_NOTIFICATIONS_PAGE
  );
  const normalizedLimit = normalizeNotificationLimit(limit);
  const {
    data: { data: notifications, totalUnseen, lastNotificationID, pagination },
  }: NotificationResponse = await axios.get('/notifications', {
    params: { page: normalizedPage, limit: normalizedLimit },
  });
  const normalizedNotifications = normalizeNotifications(notifications);

  return {
    notifications: normalizedNotifications,
    totalUnseen,
    lastNotificationID: lastNotificationID ?? 0,
    pagination:
      pagination ??
      buildFallbackPagination({
        page: normalizedPage,
        limit: normalizedLimit,
        total: normalizedNotifications.length,
      }),
  };
}

export async function getNotificationLimitedRequest(
  numberOfNotifications: number
): Promise<NotificationListResponse> {
  return getNotificationRequest({
    page: DEFAULT_NOTIFICATIONS_PAGE,
    limit: numberOfNotifications,
  });
}

export async function getNotificationUpdateRequest(id: number): Promise<{}> {
  const {
    data: { data },
  }: any = await axios.get(`/notifications/${id}`);
  return { data };
}

export async function getNotificationsReadAllRequest(): Promise<NotificationReadAllResponse> {
  const { data } = await axios.put<NotificationReadAllResponse>(
    '/notifications/read-all'
  );
  return data;
}
